import React, { useState } from "react";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Button } from "src/components/ui/Button";
import { Link } from "react-router-dom";
import { ROUTES } from "src/routes/RouteConfig";

import {
  BookOpen,
  Users,
  Target,
  CheckCircle,
  ArrowRight,
  Terminal,
  Code2,
  Wrench,
  ChevronRight,
  Sparkles
} from "lucide-react";

interface MilestoneBuild {
  title: string;
  description: string;
  skills: string[];
}

interface PythonModule {
  level: number;
  grade: string;
  phase: string;
  duration: string;
  status: "available" | "coming_soon";
  description: string;
  milestone: MilestoneBuild;
  code: string;
  terminalOutput: string;
  topics: string[];
  tutorFocus: string;
}

const pythonCurriculum: PythonModule[] = [
  {
    level: 1,
    grade: "Getting Started",
    phase: "Foundations",
    duration: "Foundations (3-4 Sessions)",
    status: "available",
    description: "Set up Python and write small programs that print, calculate, and respond to input.",
    milestone: {
      title: "Interactive Greeting & Profile Bot",
      description: "Learners code an interactive conversational terminal script that welcomes the user, requests biographical details, computes age benchmarks, and formats friendly output.",
      skills: ["Terminal I/O", "String Formatting (f-strings)", "Variables", "Error Reading"]
    },
    code: `# Level 1: First interactive program
user_name = input("What is your name? ")
birth_year = int(input("What year were you born? "))

current_year = 2026
age = current_year - birth_year

print(f"\\nWelcome to rfLearn, {user_name}!")
print(f"In {current_year}, you will celebrate {age} years.")
print("Status: Python environment verified and ready!")`,
    terminalOutput: `$ python greeting_bot.py
What is your name? Alex
What year were you born? 2010

Welcome to rfLearn, Alex!
In 2026, you will celebrate 16 years.
Status: Python environment verified and ready!`,
    topics: [
      "What Python is for and how interpreters work",
      "Setting up Python 3 and VS Code editor",
      "Writing your first script and using print()",
      "Reading tracebacks and understanding syntax errors"
    ],
    tutorFocus: "Tutor walks through terminal setup, debugs early syntax typos live, and ensures the learner understands line-by-line execution."
  },
  {
    level: 2,
    grade: "Variables and Data",
    phase: "Foundations",
    duration: "Foundations (4 Sessions)",
    status: "available",
    description: "Store and transform information so programs can remember values and make decisions later.",
    milestone: {
      title: "Smart Tip & Currency Bill Splitter",
      description: "A practical financial calculator that prompts for restaurant bill details, calculates customizable gratuity, and splits costs accurately per person.",
      skills: ["Float & Integer Math", "Type Casting int()/float()", "Variable Assignment", "Number Rounding"]
    },
    code: `# Level 2: Financial Calculator
total_bill = float(input("Enter bill amount ($): "))
tip_percentage = int(input("Tip percentage (15/18/20): "))
party_size = int(input("Number of diners: "))

tip_amount = total_bill * (tip_percentage / 100)
grand_total = total_bill + tip_amount
cost_per_person = grand_total / party_size

print(f"\\n--- Payment Summary ---")
print(f"Tip: \${tip_amount:.2f}")
print(f"Total: \${grand_total:.2f}")
print(f"Each person pays: \${cost_per_person:.2f}")`,
    terminalOutput: `$ python bill_splitter.py
Enter bill amount ($): 124.50
Tip percentage (15/18/20): 18
Number of diners: 3

--- Payment Summary ---
Tip: $22.41
Total: $146.91
Each person pays: $48.97`,
    topics: [
      "Numbers (int, float) and Booleans (True/False)",
      "Variables as computer memory slots",
      "Type conversion: float(), int(), str()",
      "String interpolation and input validation"
    ],
    tutorFocus: "Tutor provides mental models for computer memory and guides the student to catch divide-by-zero or non-numeric inputs."
  },
  {
    level: 3,
    grade: "Control Flow",
    phase: "Core Skills",
    duration: "Core Skills (4-5 Sessions)",
    status: "available",
    description: "Direct the path of a program with conditions and loops that repeat work until a goal is met.",
    milestone: {
      title: "Logic & Binary Search Guessing Game",
      description: "A fully playable number guessing game featuring randomized target values, remaining attempt counters, higher/lower hints, and win/loss states.",
      skills: ["if/elif/else Branching", "while Loops", "break & continue", "Comparison Operators"]
    },
    code: `# Level 3: Secret Number Game
import random

secret = random.randint(1, 50)
attempts = 5

print("I picked a number between 1 and 50. You have 5 tries!")

while attempts > 0:
    guess = int(input(f"Tries left [{attempts}]. Your guess: "))
    
    if guess == secret:
        print("🎉 BINGO! You discovered the secret number!")
        break
    elif guess < secret:
        print("Too low! Aim higher.")
    else:
        print("Too high! Aim lower.")
        
    attempts -= 1

if attempts == 0:
    print(f"Out of tries! The number was {secret}.")`,
    terminalOutput: `$ python guessing_game.py
I picked a number between 1 and 50. You have 5 tries!
Tries left [5]. Your guess: 25
Too low! Aim higher.
Tries left [4]. Your guess: 38
Too high! Aim lower.
Tries left [3]. Your guess: 32
🎉 BINGO! You discovered the secret number!`,
    topics: [
      "Branching execution with if, elif, and else",
      "Logical operators: and, or, not",
      "Repetition with while loops and for iterators",
      "Loop safety and termination guarantees"
    ],
    tutorFocus: "Tutor diagrams flowchart decision trees and teaches how binary search strategy solves this in fewer attempts."
  },
  {
    level: 4,
    grade: "Functions",
    phase: "Core Skills",
    duration: "Core Skills (4 Sessions)",
    status: "available",
    description: "Break problems into named pieces of work that can be reused, tested, and combined.",
    milestone: {
      title: "Modular Trivia & Math Quiz Engine",
      description: "An extensible quiz program built with modular functions for question scoring, percentage calculation, and automated letter grade evaluation.",
      skills: ["Function Definitions (def)", "Parameters & Return Values", "Local vs Global Scope", "Code Modularity"]
    },
    code: `# Level 4: Modular Quiz Engine
def grade_answer(user_ans, correct_ans):
    """Check answer and return points earned."""
    return 1 if user_ans.strip().lower() == correct_ans.lower() else 0

def calculate_percent(score, total_questions):
    """Return score as percentage."""
    return round((score / total_questions) * 100, 1)

def assign_letter(percent):
    if percent >= 90: return "A"
    if percent >= 80: return "B"
    return "C"

# Main quiz execution
points = grade_answer("paris", "Paris") + grade_answer("8", "8")
score_pct = calculate_percent(points, 2)
print(f"Score: {score_pct}% | Grade: {assign_letter(score_pct)}")`,
    terminalOutput: `$ python quiz_engine.py
[Q1] Capital of France? > Paris
[Q2] What is 2 ** 3? > 8

Score: 100.0% | Grade: A
All test assertions passed!`,
    topics: [
      "Defining functions with def and return",
      "Positional vs default keyword arguments",
      "Variable scope (local namespace vs global)",
      "Writing reusable pure functions"
    ],
    tutorFocus: "Tutor reviews code structure to eliminate copy-pasted blocks and refactors logic into clean, testable functions."
  },
  {
    level: 5,
    grade: "Collections",
    phase: "Core Skills",
    duration: "Core Skills (4 Sessions)",
    status: "available",
    description: "Group related values and look them up quickly when a program needs more than one piece of data.",
    milestone: {
      title: "Student Gradebook & Analytics Dashboard",
      description: "A data processing script that stores complex classroom records in dictionaries and lists, computing highest scorers, grade averages, and status rosters.",
      skills: ["Lists & Slicing", "Dictionary Key-Value Maps", "Iteration (for-in)", "Data Aggregation"]
    },
    code: `# Level 5: Gradebook Analytics
classroom = [
    {"name": "Maya", "scores": [92, 95, 98]},
    {"name": "Leo", "scores": [84, 88, 79]},
    {"name": "Devon", "scores": [90, 91, 94]}
]

for student in classroom:
    avg = sum(student["scores"]) / len(student["scores"])
    student["average"] = round(avg, 1)

# Find top performing student
top_student = max(classroom, key=lambda s: s["average"])
print(f"Top Performer: {top_student['name']} ({top_student['average']}%)")
print(f"Class Roster Processed: {len(classroom)} students.")`,
    terminalOutput: `$ python gradebook_analytics.py
Processing 3 student profiles...
- Maya: 95.0%
- Leo: 83.7%
- Devon: 91.7%

Top Performer: Maya (95.0%)
Class Roster Processed: 3 students.`,
    topics: [
      "Lists, tuples, dictionaries, and sets",
      "Accessing nested data structures",
      "List comprehensions and common collection patterns",
      "Dictionary lookups and key iteration"
    ],
    tutorFocus: "Tutor works through realistic multi-row datasets and teaches how to manipulate JSON-style nested collections."
  },
  {
    level: 6,
    grade: "Strings and Files",
    phase: "Applied Python",
    duration: "Applied Python (4 Sessions)",
    status: "available",
    description: "Process text and save work to files so programs can persist information between runs.",
    milestone: {
      title: "Personal Expense Logger with CSV Persistence",
      description: "A filesystem utility that reads, parses, and writes transaction records to disk in CSV format, creating persistent summaries that survive program restarts.",
      skills: ["File I/O (with open)", "CSV Module Reading/Writing", "String Formatting", "Defensive Error Catching"]
    },
    code: `# Level 6: Persistent CSV Logger
import csv

def log_expense(category, amount):
    with open("expenses.csv", "a", newline="") as f:
        writer = csv.writer(f)
        writer.writerow([category, amount])

def read_summary():
    total = 0.0
    with open("expenses.csv", "r") as f:
        reader = csv.reader(f)
        for row in reader:
            if row: total += float(row[1])
    return total

log_expense("Course Books", 45.00)
print(f"Updated expenses.csv! Total logged: \${read_summary():.2f}")`,
    terminalOutput: `$ python expense_logger.py
Logging new entry: Course Books ($45.00)
Writing to disk -> expenses.csv [OK]
Reading back history...
Updated expenses.csv! Total logged: $185.50`,
    topics: [
      "File access modes (read 'r', write 'w', append 'a')",
      "Safe file handling using context managers (with)",
      "Reading and parsing tabular CSV records",
      "String manipulation methods (split, strip, replace)"
    ],
    tutorFocus: "Tutor shows how real-world software avoids file corruption and handles missing files with try/except blocks."
  },
  {
    level: 7,
    grade: "Objects and Modules",
    phase: "Applied Python",
    duration: "Applied Python (4 Sessions)",
    status: "available",
    description: "Model real-world ideas with objects and split code into modules as programs grow.",
    milestone: {
      title: "Virtual Bank & Digital Wallet Simulator (OOP)",
      description: "An object-oriented banking application defining Account and Wallet classes, tracking balances, authorizing transactions, and enforcing business rules.",
      skills: ["Classes & Instances", "__init__ Constructors", "Methods & Attributes", "State Encapsulation"]
    },
    code: `# Level 7: Object-Oriented Banking
class BankAccount:
    def __init__(self, owner, starting_balance=0.0):
        self.owner = owner
        self.balance = starting_balance

    def deposit(self, amount):
        self.balance += amount
        return self.balance

    def withdraw(self, amount):
        if amount > self.balance:
            raise ValueError("Insufficient balance!")
        self.balance -= amount
        return self.balance

acct = BankAccount("Alex", 150.00)
acct.deposit(50.00)
print(f"Owner: {acct.owner} | Verified Balance: \${acct.balance:.2f}")`,
    terminalOutput: `$ python bank_simulation.py
Initializing BankAccount for 'Alex'...
Deposit $50.00 -> Approved
Owner: Alex | Verified Balance: $200.00
Attempt overdraw $500.00 -> ValueError caught safely!`,
    topics: [
      "Object-Oriented Programming (OOP) fundamentals",
      "Creating classes, instance variables, and methods",
      "Self parameter and constructor (__init__)",
      "Importing Python standard library modules"
    ],
    tutorFocus: "Tutor connects OOP concepts directly to college curriculum (AP Computer Science A / college CS 101 readiness)."
  },
  {
    level: 8,
    grade: "Projects and Problem Solving",
    phase: "Capstone",
    duration: "Capstone (4-6 Sessions)",
    status: "available",
    description: "Put the pieces together on projects that read data, make decisions, and produce a useful result.",
    milestone: {
      title: "Standalone CLI Productivity & Data Assistant",
      description: "A comprehensive end-to-end Python application integrating object-oriented classes, persistent JSON/CSV storage, menu navigation, automated unit tests, and defense.",
      skills: ["End-to-End Architecture", "Unit Testing (unittest)", "Defensive Programming", "Portfolio Presentation"]
    },
    code: `# Level 8 Capstone: Production CLI Suite
import json

class TaskManager:
    def __init__(self, storage_file="tasks.json"):
        self.storage = storage_file
        self.tasks = self.load()

    def load(self):
        try:
            with open(self.storage, "r") as f:
                return json.load(f)
        except FileNotFoundError:
            return []

    def add(self, title):
        self.tasks.append({"title": title, "done": False})
        with open(self.storage, "w") as f:
            json.dump(self.tasks, f, indent=2)

print("rfLearn Capstone v1.0 compiled successfully.")`,
    terminalOutput: `$ python capstone_assistant.py --test
Running test suite...
test_account_creation (__main__.TestApp) ... ok
test_data_persistence (__main__.TestApp) ... ok
test_boundary_conditions (__main__.TestApp) ... ok

----------------------------------------------------------------------
Ran 3 tests in 0.042s - OK
Capstone defense verified for student portfolio!`,
    topics: [
      "System architecture and multi-file project design",
      "Unit testing and automated test suites",
      "Defensive programming and graceful error handling",
      "Publishing code to GitHub for student portfolios"
    ],
    tutorFocus: "Tutor conducts formal code reviews, teaches git basics, and prepares the student to confidently explain their code in interviews."
  }
];

const PythonCourse: React.FC = () => {
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const [activeConsoleTab, setActiveConsoleTab] = useState<"code" | "output">("code");

  const getAvailableModulesCount = () => {
    return pythonCurriculum.filter(module => module.status === "available").length;
  };

  const currentModule = pythonCurriculum[selectedModuleIndex];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow overflow-hidden py-8 sm:py-12">
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <div className="mb-20 grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-20">
              <div className="lg:py-8">
                <h1 className="max-w-[12ch] text-5xl font-extrabold leading-[0.98] tracking-[-0.04em] text-gray-950 sm:text-6xl lg:text-7xl">
                  Learn to think in Python.
                </h1>
                <p className="mt-7 max-w-xl text-lg leading-8 text-gray-600 sm:text-xl">
                  A computer science path that starts with first programs, then builds toward real projects—one skill at a time.
                </p>
                <p className="mt-5 max-w-lg text-base leading-7 text-gray-500">
                  Our {getAvailableModulesCount()}-module Python sequence covers the language, problem-solving habits, and the tools learners need to keep going.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" className="button-gradient text-white" asChild>
                    <Link to={ROUTES.REGISTER.path}>
                      Start learning Python
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-gray-300 bg-white text-gray-800 hover:bg-rfpurple-50 hover:text-rfpurple-800"
                    asChild
                  >
                    <Link to={ROUTES.BOOK_DEMO.path}>
                      <Users className="mr-2 h-5 w-5" />
                      Find the right fit
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="relative border-y border-rfpurple-200 bg-rfpurple-50 px-6 py-7 sm:px-8 sm:py-9">
                <div className="absolute -left-px top-8 bottom-8 w-1 bg-rfpurple-600" aria-hidden="true" />
                <p className="pl-4 text-sm font-semibold text-rfpurple-800">A course path with room to grow</p>
                <div className="mt-7 divide-y divide-rfpurple-200">
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5 first:pt-0">
                    <span className="text-sm font-bold text-rfpurple-700">Start</span>
                    <div>
                      <p className="font-bold text-gray-900">Write working programs</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">Setup, variables, input, and the first programs that do something useful.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5">
                    <span className="text-sm font-bold text-rfpurple-700">Build</span>
                    <div>
                      <p className="font-bold text-gray-900">Control the logic</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">Conditions, loops, functions, and collections that scale beyond one script.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 pb-0 pt-5">
                    <span className="text-sm font-bold text-rfpurple-700">Apply</span>
                    <div>
                      <p className="font-bold text-gray-900">Ship a complete project</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">Files, modules, debugging, and a finished program you can explain.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Journey Section (Interactive Studio Explorer with Milestone Builds) */}
            <div className="mb-20">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3 sm:text-4xl">
                  Python Course Journey
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Select any level to inspect key concepts, explore the Python code, and see the exact Milestone Build learners produce with their tutor.
                </p>

                {/* Phase Pills */}
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Phase 1: Foundations (L1-2)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> Phase 2: Core Skills (L3-5)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-rfpurple-500" /> Phase 3: Applied Systems (L6-7)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-orange-500" /> Phase 4: Capstone (L8)
                  </span>
                </div>
              </div>

              {/* Split-Pane Studio Explorer Container */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* Left Module Rail (5 cols) */}
                  <div className="lg:col-span-5 space-y-3">
                    <div className="flex items-center justify-between px-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                      <span>Course Track</span>
                      <span>8 Modules</span>
                    </div>

                    <div className="space-y-2.5 max-h-[680px] overflow-y-auto pr-1">
                      {pythonCurriculum.map((mod, idx) => {
                        const isSelected = idx === selectedModuleIndex;
                        return (
                          <button
                            key={mod.level}
                            type="button"
                            onClick={() => {
                              setSelectedModuleIndex(idx);
                              setActiveConsoleTab("code");
                            }}
                            className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-start justify-between gap-3 ${
                              isSelected
                                ? "bg-rfpurple-50 border-rfpurple-500 shadow-sm ring-1 ring-rfpurple-400"
                                : "bg-white hover:bg-slate-50 border-slate-200"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`w-6 h-6 mt-0.5 rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? "bg-rfpurple-600 text-white" : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {mod.level}
                              </span>
                              <div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-xs font-bold ${isSelected ? "text-rfpurple-950" : "text-slate-900"}`}>
                                    {mod.grade}
                                  </span>
                                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                    {mod.phase}
                                  </span>
                                </div>
                                <div
                                  className={`mt-1 flex items-center gap-1 text-[11px] font-semibold ${
                                    isSelected ? "text-orange-700" : "text-slate-500"
                                  }`}
                                >
                                  <Wrench className="w-3 h-3 text-orange-500 flex-shrink-0" />
                                  <span className="truncate max-w-[210px]">{mod.milestone.title}</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight
                              className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform ${
                                isSelected ? "text-rfpurple-600 translate-x-0.5" : "text-slate-300"
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>

                    <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl text-center">
                      <p className="text-xs text-slate-600">Unsure where your learner starts?</p>
                      <Link
                        to={ROUTES.BOOK_DEMO.path}
                        className="text-xs font-bold text-rfpurple-700 hover:text-rfpurple-800 underline mt-0.5 inline-block"
                      >
                        Schedule a 1-on-1 placement diagnostic →
                      </Link>
                    </div>
                  </div>

                  {/* Right Stage Detail (7 cols) */}
                  <div className="lg:col-span-7 space-y-5">
                    <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-sm relative">
                      
                      {/* Top Header & Badges */}
                      <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-slate-100 mb-4">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-md text-xs font-mono font-bold bg-rfpurple-100 text-rfpurple-800 border border-rfpurple-200">
                            LEVEL {currentModule.level}
                          </span>
                          <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">
                            {currentModule.phase}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Available for 1-on-1 Enrollment
                        </span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-2xl font-extrabold text-slate-900 mb-2">
                        {currentModule.grade}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-5">
                        {currentModule.description}
                      </p>

                      {/* Milestone Build Box (Peach Background) */}
                      <div className="p-4 rounded-xl bg-gradient-to-r from-orange-50/90 via-amber-50/40 to-white border-2 border-orange-200/90 mb-5 relative overflow-hidden shadow-xs">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-lg bg-orange-500 text-white flex items-center justify-center text-xs font-bold shadow-xs flex-shrink-0">
                            <Wrench className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-xs font-extrabold uppercase tracking-wider text-orange-900 flex-shrink-0">
                            Milestone Build:
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {currentModule.milestone.title}
                          </span>
                        </div>

                        <p className="text-xs text-slate-700 leading-relaxed mb-3">
                          {currentModule.milestone.description}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-orange-200/60 text-[11px] text-orange-950 font-medium">
                          {currentModule.milestone.skills.map((skill) => (
                            <span
                              key={skill}
                              className="bg-white/80 px-2 py-0.5 rounded border border-orange-200"
                            >
                              ✓ {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Dual-Tab Code / Terminal Console */}
                      <div className="mb-5 bg-slate-950 rounded-xl border border-slate-800 overflow-hidden shadow-lg">
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-900 border-b border-slate-800">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setActiveConsoleTab("code")}
                              className={`px-3 py-1 text-xs font-mono font-semibold rounded transition-all flex items-center gap-1.5 ${
                                activeConsoleTab === "code"
                                  ? "bg-slate-800 text-white border border-slate-700"
                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                              }`}
                            >
                              <Code2 className="w-3.5 h-3.5 text-rfpurple-400" />
                              main.py
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveConsoleTab("output")}
                              className={`px-3 py-1 text-xs font-mono font-semibold rounded transition-all flex items-center gap-1.5 ${
                                activeConsoleTab === "output"
                                  ? "bg-slate-800 text-emerald-300 border border-slate-700"
                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                              }`}
                            >
                              <Terminal className="w-3.5 h-3.5 text-emerald-400" />
                              Milestone Run ($ python)
                            </button>
                          </div>
                          <span className="text-[11px] font-mono text-slate-500 hidden sm:inline">
                            Python 3.12
                          </span>
                        </div>

                        {activeConsoleTab === "code" ? (
                          <div className="p-4 font-mono text-xs leading-relaxed text-white overflow-x-auto">
                            <pre className="whitespace-pre text-white">{currentModule.code}</pre>
                          </div>
                        ) : (
                          <div className="p-4 font-mono text-xs leading-relaxed text-emerald-300 bg-black/95 overflow-x-auto">
                            <pre className="whitespace-pre">{currentModule.terminalOutput}</pre>
                          </div>
                        )}
                      </div>

                      {/* Core Learning Topics */}
                      <div className="mb-5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">
                          Core Competencies Mastered
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {currentModule.topics.map((topic) => (
                            <div
                              key={topic}
                              className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-700"
                            >
                              <span className="text-rfpurple-600 font-bold mt-0.5">•</span>
                              <span>{topic}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 1-on-1 Tutor Value Prop */}
                      <div className="p-3.5 bg-rfpurple-50/70 border border-rfpurple-200 rounded-xl mb-6">
                        <div className="flex items-center gap-2 text-xs font-bold text-rfpurple-900 mb-1">
                          <Sparkles className="w-4 h-4 text-rfpurple-600" />
                          <span>How 1-on-1 Tutors Support this Milestone</span>
                        </div>
                        <p className="text-xs text-rfpurple-950/80 leading-relaxed">
                          {currentModule.tutorFocus}
                        </p>
                      </div>

                      {/* Action Footer */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
                        <div className="text-xs text-slate-500">
                          Pacing: <strong className="text-slate-800">{currentModule.duration}</strong>
                        </div>
                        <Button size="sm" className="button-gradient text-white w-full sm:w-auto" asChild>
                          <Link to={ROUTES.REGISTER.path}>
                            Enroll in Level {currentModule.level}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>

                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Bottom CTA Banner */}
            <div className="bg-gradient-to-r from-rfpurple-600 to-rfpurple-500 rounded-2xl p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Coding?</h2>
              <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                Begin with Python and learn computer science with expert tutors and interactive practice.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="bg-white text-rfpurple-600 hover:bg-gray-100" asChild>
                  <Link to={ROUTES.REGISTER.path}>
                    <Target className="mr-2 h-5 w-5" />
                    Get Started Now
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-rfpurple-600 bg-transparent" asChild>
                  <Link to={ROUTES.INQUIRY.path}>
                    <BookOpen className="mr-2 h-5 w-5" />
                    Learn More
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PythonCourse;